import { ThemeProvider } from '@emotion/react';
import {
  StyledEngineProvider,
  GlobalStyles,
  Stack,
  Box,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { LoadingButton } from '@mui/lab';

// Local Imports
import { images } from './assets/images';
import { theme } from './common/theme';
import './logged-in.css';
import SYDLeftPanelContainer from './components_v2/SYDLeftPanelContainer';
import { useAppTransitions } from './ui';
import HeaderProgressBar from './components_v2/header/HeaderPogressBar';
import { ContinueRef } from './onboarding/types';
import VerificationCodeForm from './components_v2/forms/VerificationCodeForm';
import { LoggedInStatus } from './redux/loggedIn';
import { usePhoneNumberGeneticsKits } from './common/hooks';
import PhoneForm from './components_v2/forms/PhoneForm';
import QRForm from './components_v2/logged_in/QRForm';
import { useAppSelector } from './redux/store';
import { appEnvironment } from './common/environment';

// =============================================================================
// 1. Custom Hook: Logic Extraction
// =============================================================================

const useLoggedInLogic = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Redux & Custom Hooks
  const { loggedInStatus } = usePhoneNumberGeneticsKits();
  const buttonDisabled = useAppSelector(
    (state) => state.loggedIn.buttonDisabled
  );

  // Refs
  const leftPanelImageRef = useRef<HTMLImageElement>(null);
  const formRef = useRef<ContinueRef>(null);

  // Animation Hook
  const { leftPanelTranslationX } = useAppTransitions(leftPanelImageRef, false);

  // Effect: Handle Language Param
  useEffect(() => {
    const lng = searchParams.get('lng');
    if (lng !== null) {
      setSearchParams(`lng=${lng}`);
    }
  }, [searchParams, setSearchParams]);

  // Computed: Are we showing the phone/code flow?
  const showingPhoneNumber = useMemo(() => {
    return [LoggedInStatus.Phone, LoggedInStatus.VerificationCode].includes(
      loggedInStatus
    );
  }, [loggedInStatus]);

  // Computed: Header Title
  const stepTitle = useMemo(() => {
    if (loggedInStatus === LoggedInStatus.Phone) {
      return t('yourPhoneNumber');
    } else if (loggedInStatus === LoggedInStatus.VerificationCode) {
      return t('verificationCodeMessage');
    }
    return undefined;
  }, [loggedInStatus, t]);

  // Handler: Continue Button Click
  const handleContinue = () => {
    if (formRef.current) {
      formRef.current.continuePressed();
    }
  };

  return {
    t,
    loggedInStatus,
    buttonDisabled,
    leftPanelImageRef,
    formRef,
    leftPanelTranslationX,
    showingPhoneNumber,
    stepTitle,
    handleContinue,
  };
};

// =============================================================================
// 2. Sub-Components: View Extraction
// =============================================================================

interface FormContentProps {
  status: LoggedInStatus;
  formRef: React.RefObject<ContinueRef>;
}

/**
 * Renders the specific form based on the current status (QR, Phone, or Code).
 */
const FormContent = ({ status, formRef }: FormContentProps) => {
  switch (status) {
    case LoggedInStatus.QR:
      return <QRForm />;
    case LoggedInStatus.Phone:
      return <PhoneForm ref={formRef} />;
    case LoggedInStatus.VerificationCode:
      return <VerificationCodeForm ref={formRef} />;
    default:
      return null;
  }
};

interface ActionFooterProps {
  visible: boolean;
  disabled: boolean;
  onContinue: () => void;
  t: TFunction;
}

/**
 * Renders the footer with the "Continue" button and disclaimer text.
 */
const ActionFooter = ({
  visible,
  disabled,
  onContinue,
  t,
}: ActionFooterProps) => {
  if (!visible) {
    // Return a spacer to maintain layout balance if needed, or null
    return <Box className="h-[48px] my-10" />;
  }

  return (
    <Stack
      className="macbook13:my-0 macbook13:mb-[20px] my-[20px] z-20 relative"
      alignItems={'center'}
    >
      <Typography component={'span'} className="signUp-note dark">
        <Typography component={'span'} className="signUp-note">
          98%
        </Typography>{' '}
        {t('percentOfMembers').replace(/syd/gi, appEnvironment.theme.appName)}
      </Typography>

      <LoadingButton
        variant="contained"
        disabled={disabled}
        endIcon={<Box component="img" src={images.v2.button.tick} />}
        className={`button large bg-text`}
        onClick={onContinue}
      >
        {t('continue')}
      </LoadingButton>
    </Stack>
  );
};

// =============================================================================
// 3. Main Component
// =============================================================================

export default function LoggedInV2() {
  const {
    t,
    loggedInStatus,
    buttonDisabled,
    leftPanelImageRef,
    formRef,
    leftPanelTranslationX,
    showingPhoneNumber,
    stepTitle,
    handleContinue,
  } = useLoggedInLogic();

  // Determine if animation/content should be visible
  const isAnimating =
    leftPanelTranslationX !== 0 ||
    !appEnvironment.theme.showOnboardingAnimation;

  // --- View Construction (Matching LoggedOutV2 Structure) ---

  // 1. Left Panel (Visual Background)
  const leftPanel = (
    <Stack
      className={`fixed top-0 left-0 w-full h-full z-[-2] transition-transform duration-1000`}
      justifyContent="center"
      alignItems="center"
      sx={{
        transform: `translateX(${leftPanelTranslationX}px)`,
      }}
    >
      <SYDLeftPanelContainer imageRef={leftPanelImageRef} />
    </Stack>
  );

  // 2. Right Panel (Forms)
  const rightPanel = (
    <Stack
      direction="row"
      justifyContent="center"
      alignItems="center" // Vertically center the form content
      className="relative z-10 w-full mb-[10px] min-h-full flex-1"
    >
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        className="macbook13:mt-[0px] w-full"
      >
        <FormContent status={loggedInStatus} formRef={formRef} />
      </Stack>
    </Stack>
  );

  return (
    <StyledEngineProvider injectFirst={true}>
      <ThemeProvider theme={theme}>
        <GlobalStyles
          styles={{ body: { background: '#F5F6F6', padding: 0, margin: 0 } }}
        />

        <Stack className="root-container" sx={{ minHeight: '100vh' }}>
          {/* --- Header Section --- */}
          <Stack className="relative w-full justify-center my-[20px] z-20">
            {/* Logo: Absolute Top Left */}
            <Stack
              justifyContent={'center'}
              alignItems={'center'}
              className="absolute left-[40px] top-0 logo-container"
            >
              <Box
                component="img"
                src={appEnvironment.theme.images.logo}
                className="max-w-[50px] macbook13:max-w-[40px]"
              />
            </Stack>

            {/* Progress Bar (Centered) */}
            <HeaderProgressBar
              label={t('signUp')}
              step={2}
              totalSteps={2}
              subtext={t('completedExclamationMark')}
              blue={true}
              stepTitle={stepTitle}
              stepTitleExtraTopMargin={showingPhoneNumber}
            />
          </Stack>

          {/* --- Main Content Area --- */}
          <Stack direction="row" className="w-full flex-1 relative">
            {/* Wrap left panel in opacity transition logic */}
            <Box className={isAnimating ? 'opacity-100' : 'opacity-0'}>
              {leftPanel}
            </Box>

            {rightPanel}
          </Stack>

          {/* --- Footer Section --- */}
          <ActionFooter
            visible={showingPhoneNumber}
            disabled={buttonDisabled}
            onContinue={handleContinue}
            t={t}
          />
        </Stack>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
